/**
 * catalogLabels.ts
 *
 * The per-market label rule shared by the metric catalog and the distribution
 * catalog. Both surfaces carry the same wire shape by design — a canonical
 * `label` plus a sparse `labelOverrides` map keyed by market-id-as-string — so
 * they resolve through one function rather than two that can drift.
 *
 * The canonical value is always the registry default, never a requested
 * market's, so the primary and the overrides share one reference frame and
 * `overrides[marketId] ?? label` is correct for every market including the ones
 * that override.
 */

/** A catalog node carrying a canonical label and optional per-market overrides. */
export interface LabelledCatalogNode {
  label: string;
  labelOverrides?: Record<string, string>;
}

/** The one label rule: labelOverrides[marketId] ?? label. */
export function resolveLabel(
  node: LabelledCatalogNode,
  marketId: number | undefined,
): string {
  if (marketId == null) return node.label;
  return node.labelOverrides?.[String(marketId)] ?? node.label;
}

/**
 * The market a label should resolve against, given the current selection.
 *
 * Exactly one market selected → that market's words. Several selected → the
 * canonical default, because no single override can speak for a set of markets
 * that disagree (US "Consideration" vs Etsy "Word of Mouth"); picking one would
 * present one market's vocabulary as the truth for all of them. Zero selected →
 * the market is not resolved yet, which is also the canonical case.
 *
 * `resolveLabel(node, undefined)` already returns the canonical label, so this
 * is the whole of that rule.
 */
export function labelMarketId(marketIds: number[]): number | undefined {
  return marketIds.length === 1 ? marketIds[0] : undefined;
}
